/**
 * Rich Text utilities for Contentful content
 */

/**
 * Check if Rich Text JSON contains embedded entries or assets
 * Used to determine if lazy loading of links is needed
 *
 * @param {Object} json - Rich Text JSON from Contentful
 * @returns {boolean} - True if embedded content exists
 */
export const hasEmbeddedContent = (json) => {
  if (!json?.content) return false;

  const checkNode = (node) => {
    if (node.nodeType === 'embedded-entry-block' || node.nodeType === 'embedded-asset-block') {
      return true;
    }
    if (node.content) {
      return node.content.some(checkNode);
    }

    return false;
  };

  return json.content.some(checkNode);
};

/**
 * Plain text from Contentful Rich Text JSON (recursive walk; block nodes get newline separators).
 *
 * @param {Object} [document] - Rich Text `json` root document
 * @returns {string}
 */
export const richTextToPlainText = (document) => {
  if (!document || typeof document !== 'object') return '';

  const walk = (node) => {
    if (!node || typeof node !== 'object') return '';

    if (node.nodeType === 'text') {
      return node.value || '';
    }

    const childText = (node.content || []).map(walk).join('');

    if (node.nodeType === 'paragraph' || node.nodeType.startsWith('heading-')) {
      return childText ? `${childText}\n` : '';
    }

    return childText;
  };

  return walk(document).replace(/\n+/g, '\n').trim();
};

/**
 * Plain text from the first non-empty top-level block (TSD BlogPosting `description`).
 *
 * @param {Object} [document] - Rich Text `json` root document
 * @returns {string}
 */
export const richTextFirstBlockPlainText = (document) => {
  if (!document?.content?.length) return '';

  for (const block of document.content) {
    const text = richTextToPlainText({content: [block], nodeType: 'document'});

    if (text) return text;
  }

  return '';
};
